import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.choicestfruit.com";
const DEFAULT_TITLE = "Choicest Fruit®, LLC | Career School, Technology and Community Impact";
const DEFAULT_DESCRIPTION =
  "Choicest Fruit® is a social enterprise, community technology company, and a Texas Approved Career School offering technology education and workforce training.";

const ROUTE_META = [
  {
    match: (path) => path === "/",
    title: "Choicest Fruit®, LLC | Career School and Community Technology",
    description:
      "Choicest Fruit® is a Texas Approved Career School and community technology company offering workforce training, youth programs, and innovation services.",
  },
  {
    match: (path) => path.startsWith("/about"),
    title: "About Choicest Fruit® | Mission, Story and Partners",
    description:
      "Learn about Choicest Fruit®: our mission, story, and partnerships supporting ethical technology education and community-centered innovation.",
  },
  {
    match: (path) =>
      ["/courses", "/admissions", "/catalog", "/student-resources", "/tiers", "/programs/trainings-tiers"].includes(path),
    title: "Career School Programs | Choicest Fruit®",
    description:
      "Explore Choicest Fruit® Career School programs, admissions, catalog, student resources, and workforce-aligned technology training pathways.",
  },
  {
    match: (path) =>
      path === "/lab" ||
      path.startsWith("/services") ||
      [
        "/web-pricing",
        "/robotics-pricing",
        "/three-d-web-pricing",
        "/three-d-modeling-pricing",
        "/justice-tech-pricing",
        "/approp-tech-pricing",
      ].includes(path),
    title: "Technology and Innovation Services | Choicest Fruit®",
    description:
      "Discover Choicest Fruit® technology and innovation services, including consulting, digital products, and practical solutions for communities and organizations.",
  },
  {
    match: (path) => path.startsWith("/impact") || path.startsWith("/programs/"),
    title: "Community Impact Programs | Choicest Fruit®",
    description:
      "See Choicest Fruit® community impact programs, internships, youth pathways, and neighborhood initiatives that build local opportunity.",
  },
  {
    match: (path) => path === "/team",
    title: "Team | Choicest Fruit®",
    description:
      "Meet the Choicest Fruit® team supporting career training, technology education, and community-centered innovation.",
  },
  {
    match: (path) => path === "/contact",
    title: "Contact | Choicest Fruit®",
    description:
      "Contact Choicest Fruit® for career school information, partnerships, consulting, and community technology programs.",
  },
];

const BREADCRUMB_NAME_MAP = {
  "/": "Home",
  "/about": "About",
  "/about/who-we-are": "Who We Are",
  "/about/our-mission": "Our Mission",
  "/about/our-story": "Our Story",
  "/about/our-partners": "Partnerships and Collaborators",
  "/courses": "Career Training Programs",
  "/admissions": "Admissions",
  "/catalog": "Catalog",
  "/student-resources": "Student Resources",
  "/services": "Technology and Innovation",
  "/services/consulting-advisory": "Consulting and Advisory",
  "/services/favrhood": "Favrhood",
  "/impact": "Impact",
  "/impact/community": "Community Impact",
  "/team": "Team",
  "/contact": "Contact",
  "/lab": "CF Lab",
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  const selector = `meta[${attr}="${key}"]`;
  let node = document.head.querySelector(selector);
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(attr, key);
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
}

function upsertCanonical(href) {
  let node = document.head.querySelector('link[rel="canonical"]');
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", "canonical");
    node.setAttribute("id", "cf-canonical");
    document.head.appendChild(node);
  }
  node.setAttribute("href", href);
}

function upsertJsonLd(id, payload) {
  let node = document.getElementById(id);
  if (!node) {
    node = document.createElement("script");
    node.type = "application/ld+json";
    node.id = id;
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify(payload);
}

function toTitleFromPath(pathname) {
  const parts = pathname
    .split("/")
    .filter(Boolean)
    .map((part) => part.replace(/-/g, " "))
    .map((part) => part.replace(/\b\w/g, (char) => char.toUpperCase()));

  return parts.length > 0 ? `${parts.join(" | ")} | Choicest Fruit®` : DEFAULT_TITLE;
}

function breadcrumbNameForPath(path) {
  if (BREADCRUMB_NAME_MAP[path]) {
    return BREADCRUMB_NAME_MAP[path];
  }

  const segment = path.split("/").filter(Boolean).pop() || "";
  const label = segment.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
  return label || "Page";
}

function buildBreadcrumbs(pathname) {
  if (pathname === "/") {
    return [{ name: "Home", item: `${SITE_URL}/` }];
  }

  const crumbs = [{ name: "Home", item: `${SITE_URL}/` }];
  const segments = pathname.split("/").filter(Boolean);
  let currentPath = "";

  for (const segment of segments) {
    currentPath += `/${segment}`;
    crumbs.push({
      name: breadcrumbNameForPath(currentPath),
      item: `${SITE_URL}${currentPath}`,
    });
  }

  return crumbs;
}

export default function SeoManager() {
  const location = useLocation();
  const pathname = location.pathname || "/";

  const pageMeta = useMemo(() => {
    const matched = ROUTE_META.find((item) => item.match(pathname));
    if (matched) return matched;

    return {
      title: toTitleFromPath(pathname),
      description: DEFAULT_DESCRIPTION,
    };
  }, [pathname]);

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;

    document.title = pageMeta.title;
    upsertMeta("name", "description", pageMeta.description);
    upsertMeta("property", "og:title", pageMeta.title);
    upsertMeta("property", "og:description", pageMeta.description);
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", pageMeta.title);
    upsertMeta("name", "twitter:description", pageMeta.description);
    upsertCanonical(canonicalUrl);

    upsertJsonLd("cf-org-ld", {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Choicest Fruit®, LLC",
      url: SITE_URL,
      logo: `${SITE_URL}/favicon.png`,
      sameAs: [
        "https://www.youtube.com/@ChoicestFruit",
        "https://www.facebook.com/profile.php?id=100083371838989",
        "https://www.linkedin.com/company/choicest-fruit/",
      ],
      contactPoint: [
        {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: "info@choicestfruit.com",
          areaServed: "US",
          availableLanguage: ["English", "Spanish"],
        },
      ],
    });

    upsertJsonLd("cf-website-ld", {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Choicest Fruit®, LLC",
      url: SITE_URL,
      inLanguage: ["en", "es"],
    });

    const breadcrumbs = buildBreadcrumbs(pathname);
    upsertJsonLd("cf-breadcrumb-ld", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: crumb.item,
      })),
    });
  }, [pathname, pageMeta]);

  return null;
}
